/**
 * LiveGuidance hook - Simplified implementation
 * This hook manages communication with the FeedbackServer and updates the ScriptStore
 * based on server responses.
 */
import { useEffect, useMemo, useState } from "react";

import type { BuyingSignalMessage } from "@frontierx/shared/schemas/BuyingSignal";
import type { DigDeeperOpportunityMessage } from "@frontierx/shared/schemas/DigDeeperOpportunity";
import { FeedbackMessageSchema } from "@frontierx/shared/schemas/FeedbackMessage";
import type {
  ScriptItemInsertion,
  ScriptItemInsertionMessage,
} from "@frontierx/shared/schemas/ScriptItemInsertion";
import type { ClientMessage } from "@frontierx/shared/schemas/clientMessageTypes";

import { useToast } from "@/components/hooks/use-toast";
import type PartySocket from "partysocket";
import type { PartySocketOptions } from "partysocket";
import { useAppPartySocket } from "~/app/hooks/useAppPartySocket";
import {
  type TranscriptEvent,
  useScriptStore,
} from "../components/LiveGuidance/ScriptStore";

interface UseLiveGuidanceOptions {
  isEnabled?: boolean;
  debug?: boolean;
  roomId?: string;
  hostName?: string;
  companyName?: string;
  botSocket?: PartySocket; // For backward compatibility
}

/**
 * LiveGuidance hook providing AI-powered guidance during calls.
 * Supports both positional parameters (for backward compatibility)
 * and options object pattern.
 */
export function useLiveGuidance(
  roomIdOrOptions: string | UseLiveGuidanceOptions = {},
  externalBotSocket?: PartySocket,
  isEnabledParam?: boolean,
  hostNameParam = "SDR",
  companyNameParam = "Frontier AI",
) {
  // Handle both parameter styles for backward compatibility
  let options: UseLiveGuidanceOptions;

  if (typeof roomIdOrOptions === "string") {
    // Handle legacy positional parameters pattern
    options = {
      roomId: roomIdOrOptions || "",
      botSocket: externalBotSocket,
      isEnabled: isEnabledParam ?? true,
      hostName: hostNameParam,
      companyName: companyNameParam,
      debug: false,
    };
  } else {
    // Handle new options object pattern
    options = roomIdOrOptions;
  }

  // Extract options with defaults
  const {
    isEnabled = true,
    debug = false,
    roomId = "",
    hostName = "SDR",
    companyName = "Frontier AI",
    botSocket: externalBotSocketFromOptions,
  } = options;

  const [initialized, setInitialized] = useState(false);
  const { toast } = useToast();
  const store = useScriptStore();

  // Conditionally invoke useAppPartySocket
  const guidanceSocketInstance =
    isEnabled && roomId
      ? useAppPartySocket({
          party: "feedback",
          room: roomId,
          onMessage: handleMessageFromServer,
        })
      : null;

  const internalBotSocketInstance =
    isEnabled && roomId
      ? useAppPartySocket({
          party: "bot",
          room: roomId,
        })
      : null;

  // Assign to constants for use in effects and return (matches original naming)
  const guidanceSocket = guidanceSocketInstance;
  const internalBotSocket = internalBotSocketInstance;

  const botSocket =
    externalBotSocketFromOptions ?? externalBotSocket ?? internalBotSocket;

  // Initialisation useEffect now correctly depends on guidanceSocket instance
  useEffect(() => {
    if (!isEnabled || !roomId || !guidanceSocket) {
      if (debug && isEnabled && !roomId)
        console.log("[useLiveGuidance] Initialization skipped: no roomId");
      if (debug && !isEnabled)
        console.log(
          "[useLiveGuidance] Initialization skipped: isEnabled is false (guidanceSocket not created)",
        );
      if (debug && isEnabled && roomId && !guidanceSocket)
        console.log(
          "[useLiveGuidance] Initialization skipped: guidanceSocket is null (hook returned null)",
        );
      return;
    }

    setInitialized(true);
    store.setHasActiveCall(true);

    try {
      if (debug) console.log("[useLiveGuidance] Sending INITIALIZE_GUIDANCE");
      guidanceSocket.send(
        JSON.stringify({
          type: "INITIALIZE_GUIDANCE",
          payload: { hostName, companyName, timestamp: Date.now() / 1000 },
          timestamp: new Date().toISOString(),
        }),
      );
    } catch (error) {
      console.error(
        "[useLiveGuidance] Error sending initialization message:",
        error,
      );
    }

    if (debug)
      console.log(
        "[useLiveGuidance] Initialized guidance service for room:",
        roomId,
      );

    return () => {
      store.setHasActiveCall(false);
      if (debug) console.log("[useLiveGuidance] Cleaned up guidance service");
    };
  }, [
    isEnabled,
    roomId,
    hostName,
    companyName,
    debug,
    store.setHasActiveCall,
    guidanceSocket,
  ]);

  function handleMessageFromServer(event: MessageEvent) {
    if (!isEnabled) {
      if (debug)
        console.log(
          "[useLiveGuidance] Message received but isEnabled is false, ignoring.",
        );
      return;
    }
    try {
      const data = JSON.parse(event.data);
      const message = FeedbackMessageSchema.safeParse(data);

      if (!message.success) {
        console.error("[useLiveGuidance] Invalid message format:", data);
        return;
      }
      if (message.data.type !== "conversation_update" && debug) {
        console.log("[useLiveGuidance] Message from server:", message.data);
      }
      switch (message.data.type) {
        case "buying_signal_detected":
          handleBuyingSignal(message.data as BuyingSignalMessage);
          break;
        case "script_item_insertion":
          handleScriptItemInsertion(message.data as ScriptItemInsertionMessage);
          break;
        case "dig_deeper_opportunity":
          handleDigDeeperOpportunity(
            message.data as DigDeeperOpportunityMessage,
          );
          break;
        case "conversation_update":
          break;
      }
    } catch (error) {
      console.error(
        "[useLiveGuidance] Error processing server message:",
        error,
      );
    }
  }

  function handleBuyingSignal(message: BuyingSignalMessage) {
    toast({ title: "Buying Signal!", description: message.data.explanation });
  }

  function handleScriptItemInsertion(message: ScriptItemInsertionMessage) {
    toast({ title: "Adding Script Item", description: message.data.text });
    store.insertScriptItem(message.data.text, undefined, {
      explanation: message.data.explanation,
      confidence: message.data.confidence,
    });

    if (botSocket) {
      const botPayload: ScriptItemInsertion = {
        text: message.data.text,
        explanation: message.data.explanation,
        confidence: message.data.confidence,
      };
      const insertMessage: ClientMessage = {
        type: "INSERT_SCRIPT_ITEM",
        payload: botPayload,
        timestamp: new Date().toISOString(),
      };
      try {
        if (debug)
          console.log(
            "[useLiveGuidance] Forwarding INSERT_SCRIPT_ITEM to bot socket",
            insertMessage,
          );
        botSocket.send(JSON.stringify(insertMessage));
      } catch (error) {
        console.error(
          "[useLiveGuidance] Error forwarding message to bot socket:",
          error,
        );
      }
    } else {
      if (debug)
        console.warn(
          "[useLiveGuidance] Bot socket not available, cannot forward INSERT_SCRIPT_ITEM",
        );
    }
  }

  function handleDigDeeperOpportunity(message: DigDeeperOpportunityMessage) {
    toast({ title: "Dig deeper?", description: message.data.opportunity });
  }

  useEffect(() => {
    if (!isEnabled || !initialized || !guidanceSocket) return;

    const handleReconnection = (event: Event) => {
      const reconnEvent = event as CustomEvent<{
        room: string;
        party: string;
        timestamp: number;
      }>;
      if (reconnEvent.detail.room !== roomId) return;
      if (debug)
        console.log("[useLiveGuidance] Reconnection event for room:", roomId);
      store.setHasActiveCall(true);
      try {
        if (debug) console.log("[useLiveGuidance] Sending RECONNECT_GUIDANCE");
        guidanceSocket.send(
          JSON.stringify({
            type: "RECONNECT_GUIDANCE",
            payload: { timestamp: Date.now() / 1000 },
            timestamp: new Date().toISOString(),
          }),
        );
      } catch (error) {
        console.error(
          "[useLiveGuidance] Error sending reconnection message:",
          error,
        );
      }
    };
    window.addEventListener("partysocket:reconnected", handleReconnection);
    return () => {
      window.removeEventListener("partysocket:reconnected", handleReconnection);
    };
  }, [
    roomId,
    isEnabled,
    initialized,
    debug,
    store.setHasActiveCall,
    guidanceSocket,
  ]);

  function processTranscriptEvent(event: TranscriptEvent) {
    if (!initialized || !guidanceSocket || !isEnabled) {
      if (debug && !initialized)
        console.warn(
          "[useLiveGuidance] Service not initialized, can't process event",
        );
      if (debug && !guidanceSocket)
        console.warn(
          "[useLiveGuidance] Guidance socket not available, can't process event",
        );
      if (debug && !isEnabled)
        console.warn("[useLiveGuidance] Service disabled, can't process event");
      return;
    }
    try {
      store.addTranscriptEvent(event);
      if (debug)
        console.log("[useLiveGuidance] Sending TRANSCRIPT_EVENT", event);
      guidanceSocket.send(
        JSON.stringify({
          type: "TRANSCRIPT_EVENT",
          payload: event,
          timestamp: new Date().toISOString(),
        }),
      );
      if (debug)
        console.log("[useLiveGuidance] Processed transcript event:", event);
    } catch (error) {
      console.error(
        "[useLiveGuidance] Error processing transcript event:",
        error,
      );
    }
  }

  return {
    processTranscriptEvent,
    isInitialized: initialized && isEnabled,
    guidanceSocket,
    botSocket,
  };
}
